import { useCallback, useSyncExternalStore } from 'react';

import { recordConsentReceipt } from './consentReceipt';
import { consentStore } from './consentStore';
import { CATEGORIES, CONSENT_ACTIONS, OPT_IN_CATEGORIES, VENDOR_COOKIES } from './constants';

/**
 * Deletes the first-party vendor cookies we can reach.
 *
 * Third-party cookies on clarity.ms and bing.com are not reachable from this
 * origin; the page reload that follows withdrawal is what actually stops
 * further collection, since an injected tag cannot be un-injected.
 */
export function clearVendorCookies() {
  if (typeof document === 'undefined') {
    return;
  }
  for (const name of VENDOR_COOKIES) {
    document.cookie = `${name}=; Max-Age=0; Path=/`;
  }
}

/**
 * Binds React to the consent store.
 *
 * useSyncExternalStore is the correct primitive for an external mutable store:
 * it gives tearing-free reads without needing a context provider, so nothing is
 * added to the App provider stack. The server snapshot returns null — this is a
 * client-rendered SPA, and "no consent" is the only safe pre-hydration answer.
 */
export function useConsent(store = consentStore) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, () => null);

  const has = useCallback((category) => store.has(category), [store]);

  const commit = useCallback(
    (categories, action) => {
      const previous = store.getSnapshot();
      const next = store.save(categories);
      recordConsentReceipt({
        state: next,
        action: action || (previous ? CONSENT_ACTIONS.UPDATED : CONSENT_ACTIONS.GRANTED),
      });
      return next;
    },
    [store],
  );

  const acceptAll = useCallback(
    () => commit(Object.fromEntries(OPT_IN_CATEGORIES.map((category) => [category, true]))),
    [commit],
  );

  const rejectAll = useCallback(
    () => commit(Object.fromEntries(OPT_IN_CATEGORIES.map((category) => [category, false]))),
    [commit],
  );

  const save = useCallback((categories) => commit(categories), [commit]);

  const withdraw = useCallback(() => {
    const next = commit({}, CONSENT_ACTIONS.WITHDRAWN);
    clearVendorCookies();
    return next;
  }, [commit]);

  return {
    state,
    hasDecided: state !== null,
    categories: state?.categories ?? { [CATEGORIES.NECESSARY]: true },
    has,
    acceptAll,
    rejectAll,
    save,
    withdraw,
  };
}
